//
// Copyright (c) 2011-2020 by Curalate, Inc.
//
import moment, { Moment } from 'moment-timezone';

import DateString from '@models/DateString';
import { IUnisonDateRange } from '@models/UnisonTypes';
import { Opaque } from '@utils/opaque';

/*
 * An interval represents a period of time between two instants.
 * The end instant must always be greater than or equal to the start instant.
 *
 * The interval should be formatted as 'startDate/endDate' using ISO 8601 strings (time component optional).
 * e.g. 'YYYY-MM-DD/YYYY-MM-DD'
 */

type Interval = Opaque<string, 'Interval'>;

const DAY_FORMAT = 'YYYY-MM-DD';

/**
 * An interval represents a period of time between two instants.
 * The end instant must always be greater than or equal to the start instant.
 *
 * @param interval Time interval string, formatted as 'startDate/endDate',
 *                 using ISO 8601 strings (time component optional).
 * @example '2017-10-15/2017-10-31' or '2017-10-15T00:00:00Z/2017-10-31T00:00:00Z'
 * @deprecated Prefer helper methods for creating Intervals over manual instantiation.
 */
function IntervalFactory(interval: string) {
  return interval as Interval;
}

const Interval = Object.assign(IntervalFactory, {
  fromMoments(
    startMoment: Moment,
    endMoment: Moment,
    includeTimestamp = false,
  ): Interval {
    const format = includeTimestamp ? undefined : DAY_FORMAT;
    const startString = startMoment.format(format);
    const endString = endMoment.format(format);
    return Interval(`${startString}/${endString}`);
  },
  /**
   * @note Unison Date Range timestamps are always set to start-of-day in the browser timezone
   */
  fromDateRange(dateRange: IUnisonDateRange): Interval {
    return Interval.fromMoments(
      moment(dateRange.startDate),
      moment(dateRange.endDate),
      false,
    );
  },
  lengthInDays(interval: Interval): number {
    // Date-based intervals are from start of start-date to start of end-date
    const [start, end] = interval.split('/').map((d) => moment(d)) as [
      Moment,
      Moment,
    ];
    return Math.ceil(moment.duration(end.diff(start)).asDays());
  },
  fromDateStrings(date1: DateString, date2: DateString): Interval {
    return Interval(`${date1}/${date2}`);
  },
  toDateStrings(interval: Interval): [DateString, DateString] {
    const [date1, date2] = interval.split('/');
    return [date1 as DateString, date2 as DateString];
  },
});

export default Interval;
